import type { CovalChartSpecV1 } from "@/app-covi"

/** Exports are written in English, matching the app's `<html lang>`. */
export const NOTEBOOK_EXPORT_LANGUAGE = "en"

export type ExportHeadingLevel = 3 | 4 | 5 | 6

/**
 * The export's title is level 1 and each section heading level 2, so a
 * Markdown heading inside a section body starts at level 3.
 */
export function markdownHeadingLevel(depth: number): ExportHeadingLevel {
  return Math.min(6, Math.max(3, Math.trunc(depth) + 2)) as ExportHeadingLevel
}

/** Alt text for a chart image. Its complete values follow it as a table. */
export function notebookChartAlt(spec: CovalChartSpecV1): string {
  const kind = `${spec.chart.charAt(0).toUpperCase()}${spec.chart.slice(1)}`
  const series = spec.series.map((item) => item.name ?? item.field).join(", ")
  const rows = `${spec.data.length} ${spec.data.length === 1 ? "row" : "rows"}`
  return [
    `${kind} chart: ${spec.title}.`,
    spec.subtitle ? `${spec.subtitle}.` : "",
    `${rows} of ${series}. The values follow in a table.`,
  ]
    .filter(Boolean)
    .join(" ")
}

/** Bounded source rows recoverable from a saved chart that failed validation. */
export function recoverChartSourceRows(spec: unknown): {
  fields: string[]
  rows: Array<Record<string, unknown>>
} {
  const rawData =
    spec &&
    typeof spec === "object" &&
    "data" in spec &&
    Array.isArray(spec.data)
      ? spec.data
      : []
  const rows = rawData
    .filter(
      (row): row is Record<string, unknown> =>
        row !== null && typeof row === "object" && !Array.isArray(row),
    )
    .slice(0, 500)
  const fields = [...new Set(rows.flatMap((row) => Object.keys(row)))].slice(
    0,
    30,
  )
  return { fields, rows }
}

/** A source value as text, capped at 2,000 characters. A missing value is empty. */
export function sourceValueText(value: unknown): string {
  if (value === null || value === undefined) {
    return ""
  }
  return (typeof value === "string" ? value : JSON.stringify(value)).slice(
    0,
    2_000,
  )
}

/**
 * Chromium-based browsers (Chrome, Edge) write a tagged PDF from the print
 * dialog; other browsers write an untagged one.
 */
export function printsTaggedPdf(): boolean {
  const brands = (
    navigator as Navigator & {
      userAgentData?: { brands?: Array<{ brand: string }> }
    }
  ).userAgentData?.brands
  return !!brands?.some(({ brand }) => brand === "Chromium")
}

export const UNTAGGED_PRINT_NOTICE =
  "This browser saves an untagged PDF. Use Chrome or Edge for a tagged, accessible file."
